import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Palette, Rocket, ShoppingBag } from 'lucide-react';
import { ComponentType, CSSProperties, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { CollapsibleCard } from '@/Domain/components/Shared/CollapsibleCard/CollapsibleCard';
import { BookTextIcon } from '@/Domain/components/icons/BookTextIcon';
import { ConnectIcon } from '@/Domain/components/icons/ConnectIcon';
import {
  type AppOption,
  type ChannelOption,
  APP_OTHER_API_ID,
  APPS,
  CHANNEL_CHAT_EMBED,
  CHANNEL_CHAT_PAGE,
  CHANNEL_CHAT_WIDGET,
  CHANNEL_SHOPIFY,
  CHANNELS,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';
import type { GettingStartedProgressDto } from '@/UI/GettingStarted/components/useGettingStartedOnboarding';
import { INSTALL_METHOD_QUERY_PARAM } from '@/UI/Website/Install/installSnippets';

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

// The `?tab=` param the Your Website page reads; `?install_method=` opens an install sheet.
const WEBSITE_TAB_QUERY_PARAM = 'tab';
const WEBSITE_APPEARANCE_TAB = 'appearance';
const WEBSITE_INSTALL_TAB = 'install';

const ALWAYS_SHOWN_DEPLOYMENT_CHANNELS = [
  CHANNEL_CHAT_WIDGET,
  CHANNEL_CHAT_EMBED,
  CHANNEL_CHAT_PAGE,
];

export const PostOnboardingChecklist = ({ isShopifyUser = false }: { isShopifyUser?: boolean }) => {
  const { t } = useTranslation('onboarding_progress');
  const navigate = useNavigate();

  const { data: progress, isLoading } = useQuery<GettingStartedProgressDto | null>({
    queryKey: [QueryKey.gettingStarted.progress],
  });

  const selectedApps = useMemo<AppOption[]>(() => {
    const picked = new Set(progress?.external_apps_selection ?? []);
    // `other_api` is the one id that stands for a different product every time it is picked — a
    // self-hosted Jira, a Confluence, a homegrown endpoint — so its static "Other API" label
    // drops the identity the user already gave us. `external_apps_other_text` is where that name
    // is kept, whether the user typed it in the External apps step or the onboarding SaaS-remedy
    // hint wrote it, so read it back and let the row name its product.
    const otherApiName = (progress?.external_apps_other_text ?? '').trim();
    return APPS.filter((app) => picked.has(app.id)).map((app) =>
      app.id === APP_OTHER_API_ID && otherApiName
        ? { ...app, label: otherApiName, labelKey: undefined }
        : app,
    );
  }, [progress?.external_apps_selection, progress?.external_apps_other_text]);

  const deploymentChannelsToShow = useMemo<ChannelOption[]>(() => {
    const idsToShow = new Set<string>([
      ...ALWAYS_SHOWN_DEPLOYMENT_CHANNELS,
      ...(progress?.deployment_selection ?? []),
    ]);
    return CHANNELS.filter((channel) => idsToShow.has(channel.id));
  }, [progress?.deployment_selection]);

  const hasUserDeploymentPicks = (progress?.deployment_selection ?? []).length > 0;

  // Accordion steps open on hover (wrapper handlers below) and close on mouse-leave.
  // A click/tap only ever opens (never toggles closed) so it works on touch/keyboard
  // and a click while hovered can't hide the revealed CTAs. Nothing is expanded by
  // default. Step 1 is a direct link, not an accordion.
  const [expanded, setExpanded] = useState<string | null>(null);

  const go = (routePath: string, queryParams?: Record<string, string>) => {
    const qs = queryParams ? new URLSearchParams(queryParams).toString() : '';
    navigate(qs ? `${routePath}?${qs}` : routePath, { viewTransition: true });
  };

  const goToItem = (item: SelectionItem) => {
    const params: Record<string, string> = { ...(item.queryParams ?? {}) };
    if (item.autoOpenModal) params[OPEN_MODAL_QUERY_PARAM] = item.id;
    go(item.routePath, Object.keys(params).length ? params : undefined);
  };

  // A row is either a direct link (single-action, chevron-right, one click) or an
  // accordion that opens on hover. Step 2 (external apps) is only a link when the
  // user picked no apps — otherwise it lists them and stays an accordion.
  type ChecklistRow = {
    id: string;
    icon: ReactNode;
    title: string;
    description: string;
    headerTestId: string;
  } & ({ kind: 'link'; onActivate: () => void } | { kind: 'accordion'; content: ReactNode });

  const shopifyRows: ChecklistRow[] = [
    {
      id: 'shopify_install',
      icon: <ShoppingBag className="size-4 text-muted-foreground" />,
      title: t('next_steps.shopify.install_app.title'),
      description: t('next_steps.shopify.install_app.description'),
      headerTestId: 'next-steps-shopify-install-cta',
      kind: 'link',
      onActivate: () => go(IndexPath.channels, { [OPEN_MODAL_QUERY_PARAM]: CHANNEL_SHOPIFY }),
    },
    {
      id: 'shopify_customize',
      icon: <Palette className="size-4 text-muted-foreground" />,
      title: t('next_steps.shopify.customize_widget.title'),
      description: t('next_steps.shopify.customize_widget.description'),
      headerTestId: 'next-steps-shopify-customize-cta',
      kind: 'link',
      onActivate: () =>
        go(IndexPath.website, { [WEBSITE_TAB_QUERY_PARAM]: WEBSITE_APPEARANCE_TAB }),
    },
    {
      id: 'shopify_add_widget',
      icon: <Rocket className="size-4 text-muted-foreground" />,
      title: t('next_steps.shopify.add_widget.title'),
      description: t('next_steps.shopify.add_widget.description'),
      headerTestId: 'next-steps-shopify-add-widget-cta',
      kind: 'link',
      onActivate: () =>
        go(IndexPath.website, {
          [WEBSITE_TAB_QUERY_PARAM]: WEBSITE_INSTALL_TAB,
          [INSTALL_METHOD_QUERY_PARAM]: CHANNEL_SHOPIFY,
        }),
    },
  ];

  const externalAppsRow: ChecklistRow =
    selectedApps.length > 0
      ? {
          id: 'external_apps',
          icon: <ConnectIcon size={16} className="text-muted-foreground" />,
          title: t('next_steps.external_apps.title'),
          description: t('next_steps.external_apps.description'),
          headerTestId: 'next-steps-row-header-external_apps',
          kind: 'accordion',
          content: (
            <SelectionList
              items={selectedApps}
              ctaLabel={t('next_steps.cta_connect')}
              onItemClick={goToItem}
              testIdPrefix="next-steps-external-apps-item"
            />
          ),
        }
      : {
          id: 'external_apps',
          icon: <ConnectIcon size={16} className="text-muted-foreground" />,
          title: t('next_steps.external_apps.title'),
          description: t('next_steps.external_apps.description'),
          headerTestId: 'next-steps-external-apps-empty-cta',
          kind: 'link',
          onActivate: () => go(IndexPath.actions),
        };

  const standardRows: ChecklistRow[] = [
    {
      id: 'knowledge_base',
      icon: <BookTextIcon size={16} className="text-muted-foreground" />,
      title: t('next_steps.knowledge_base.title'),
      description: t('next_steps.knowledge_base.description'),
      headerTestId: 'next-steps-knowledge-base-cta',
      kind: 'link',
      onActivate: () => go(IndexPath.knowledgeBase.root),
    },
    externalAppsRow,
    {
      id: 'deployment',
      icon: <Rocket className="size-4 text-muted-foreground" />,
      title: t('next_steps.deployment.title'),
      description: t('next_steps.deployment.description'),
      headerTestId: 'next-steps-row-header-deployment',
      kind: 'accordion',
      content: (
        <div className="flex flex-col gap-3">
          <SelectionList
            items={deploymentChannelsToShow}
            ctaLabel={t('next_steps.cta_set_up')}
            onItemClick={goToItem}
            testIdPrefix="next-steps-deployment-item"
          />
          {!hasUserDeploymentPicks && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => go(IndexPath.channels)}
              className="self-start"
              data-testid="next-steps-deployment-empty-cta"
            >
              {t('next_steps.deployment.empty_cta')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const rows: ChecklistRow[] = isShopifyUser ? shopifyRows : standardRows;

  if (isLoading) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="post-onboarding-checklist">
      <h2 className="text-sm font-semibold text-foreground">{t('next_steps.heading')}</h2>
      <div className="flex flex-col gap-2">
        {rows.map((row) =>
          row.kind === 'link' ? (
            <CollapsibleCard
              key={row.id}
              icon={row.icon}
              title={row.title}
              description={row.description}
              onActivate={row.onActivate}
              testId={`next-steps-row-${row.id}`}
              headerTestId={row.headerTestId}
            />
          ) : (
            <div
              key={row.id}
              onMouseEnter={() => setExpanded(row.id)}
              onMouseLeave={() => setExpanded((current) => (current === row.id ? null : current))}
            >
              <CollapsibleCard
                icon={row.icon}
                title={row.title}
                description={row.description}
                open={expanded === row.id}
                onToggle={() => setExpanded(row.id)}
                testId={`next-steps-row-${row.id}`}
                headerTestId={row.headerTestId}
              >
                {row.content}
              </CollapsibleCard>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

interface SelectionItem {
  id: string;
  label: string;
  labelKey?: string;
  icon: Glyph;
  iconColor?: string;
  routePath: string;
  autoOpenModal: boolean;
  queryParams?: Record<string, string>;
}

const SelectionList = ({
  items,
  ctaLabel,
  onItemClick,
  testIdPrefix,
}: {
  items: SelectionItem[];
  ctaLabel: string;
  onItemClick: (item: SelectionItem) => void;
  testIdPrefix: string;
}) => {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center gap-2 py-1"
          data-testid={`${testIdPrefix}-${item.id}`}
        >
          <div className="flex size-6 items-center justify-center shrink-0">
            <item.icon
              className="size-4"
              style={item.iconColor ? { color: item.iconColor } : undefined}
            />
          </div>
          <span className="flex-1 text-sm text-foreground truncate">
            {item.labelKey ? t(item.labelKey) : item.label}
          </span>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onItemClick(item)}
            data-testid={`${testIdPrefix}-${item.id}-cta`}
          >
            {ctaLabel}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ul>
  );
};
